/* eslint-disable max-len */
/* eslint-disable no-await-in-loop */
import { Fragment, h } from 'preact';
import { useEffect, useMemo, useState, useRef } from 'preact/hooks';
import { formatMoney, edgesToNodes } from 'utils/storefront/format-resource';
import * as fragments from 'utils/storefront/fragments';
import formatProductTitle from 'utils/formatProductTitle';
import useWindowSize from 'utils/useWindowSize';
import { IconArrow, IconStar, IconHalfStar } from 'utils/icon';
import Storefront from 'utils/storefront';
import { formatProductData } from 'utils/dataLayerProductData';
import VariantSelector from './variant-selector';
import FitText from './fit-text';

const generateLineItemIdentifier = (
  itemIDs,
  attributes = {},
  delimiter = '|',
) => {
  const itemIdentifiers = itemIDs.filter(Boolean);
  const itemAttributes = attributes;
  const identifiers = [
    ...itemIdentifiers,
    ...itemAttributes.map((attr) => attr.value),
  ];
  return identifiers.join(delimiter);
};

const handleScrollToOptions = () => {
  const optionsSection = document.querySelector('[data-options-product]');
  if (optionsSection) {
    const header = document.querySelector('[data-section="header"]');
    // eslint-disable-next-line max-len
    const targetOffset = optionsSection.offsetTop - header.offsetHeight - 30;
    window.scrollTo({
      top: targetOffset,
      behavior: 'smooth',
    });
  }
};

const Price = ({ currentVariant, product, finalSaleTag }) => {
  const variant = currentVariant?.node || currentVariant;
  const finalSaleMetafield =
    product.metafields?.finalSale || product?.finalSale?.value;
  const isFinalSale = finalSaleMetafield || product.tags.includes(finalSaleTag);

  return (
    variant && (
      <div className="">
        {variant.compareAtPrice &&
          parseFloat(variant.compareAtPrice.amount) > 0 && (
            <del className="inline-block text-text-disabled mr-2 product-price--strike-through">
              {formatMoney(variant.compareAtPrice)}
            </del>
          )}
        <span
          className={`h4 ${
            variant.compareAtPrice &&
            parseFloat(variant.compareAtPrice.amount) > 0
              ? 'text-sale'
              : ''
          }`}
        >
          {formatMoney(variant.price)}
        </span>
        {isFinalSale && (
          <span className="pl-[6px] text-sale body-3 font-GTAmerica">
            {window.TMW.Strings.product.finalSalePDP}
          </span>
        )}
      </div>
    )
  );
};

const InventoryMessage = ({ currentVariant, threshold, sizeSelected }) => {
  if (
    !sizeSelected ||
    !currentVariant ||
    !currentVariant.quantityAvailable ||
    currentVariant.quantityAvailable > threshold ||
    currentVariant.quantityAvailable < 1
  )
    return false;

  return (
    <span className="pl-2.5 text-sale">
      Only {currentVariant.quantityAvailable} left!
    </span>
  );
};

const AddToCartButton = ({
  currentVariant,
  selectedOptions,
  sizeSelected,
  product,
  showPrice,
  monogramData,
  setMonogramData,
  qty,
  isSticky = false,
  isStylitics,
  discountCodeTag,
  addToCartAttributeName,
  finalSaleTag,
  dropShipTag,
  listId,
  listName,
}) => {
  const { isMobile } = useWindowSize();
  const finalSaleMetafield =
    product.metafields?.finalSale || product?.finalSale?.value;

  const handleAddToCart = (e) => {
    e.preventDefault();
    const origin = isStylitics ? 'shop_the_look_modal' : 'quick_view';

    let payload = {
      merchandiseId: currentVariant?.id,
      quantity: qty || 1,
      attributes: [],
    };

    if (finalSaleMetafield || product.tags.includes(finalSaleTag)) {
      const tagArr = [
        {
          key: '_tag_:final-sale',
          value: finalSaleMetafield || `${finalSaleTag.replace(' ', '')}`,
        },
      ];
      payload.attributes = [...payload.attributes, ...tagArr];
    }

    if (product.tags.includes(discountCodeTag)) {
      const tagObj = {
        key: '_tag_:discount-code',
        value: `${discountCodeTag}`,
      };
      if (payload.attributes && payload.attributes.length > 0) {
        payload.attributes.push(tagObj);
      } else {
        payload.attributes = [tagObj];
      }
    }

    if (product.tags.includes(dropShipTag)) {
      const tagObj = {
        key: '_tag_:drop-ship',
        value: `${dropShipTag.replace(' ', '')}`,
      };
      if (payload.attributes && payload.attributes.length > 0) {
        payload.attributes.push(tagObj);
      } else {
        payload.attributes = [tagObj];
      }
    }

    if (
      currentVariant?.compareAtPrice &&
      currentVariant?.compareAtPrice?.amount !== 0
    ) {
      payload.attributes.push({
        key: '_compare_at_price',
        value: parseFloat(currentVariant?.compareAtPrice?.amount).toFixed(2),
      });
    }

    if (monogramData) {
      const hasMonogram = monogramData.attributes?.find(
        (attr) => attr.key === '_monogram_type' && attr.value === 'monograms',
      );
      if (hasMonogram)
        payload.attributes.push({
          key: '_tag_:final-sale',
          value: finalSaleMetafield || `${finalSaleTag.replace(' ', '')}`,
        });
      payload = [payload, monogramData];
      const identifier = generateLineItemIdentifier(
        payload.map(
          ({ merchandiseId }) => merchandiseId.split('/ProductVariant/')[1],
        ),
        payload[1].attributes,
      );
      payload.forEach((item) => {
        item.attributes.push({
          key: '_groupID',
          value: identifier,
        });
      });

      // Move monogram properties into main product payload
      // Ensure quantity is set to parent product quantity
      const [parentProduct, monogramProduct] = payload;
      monogramProduct.quantity = parentProduct.quantity;
      const filteredAttributes = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < monogramProduct.attributes.length; i++) {
        const attr = monogramProduct.attributes[i];
        if (attr.key === '_monogram_data') {
          parentProduct.attributes.push(attr);
        } else {
          filteredAttributes.push(attr);
        }
      }
      monogramProduct.attributes = filteredAttributes;
    }

    if (isStylitics) {
      payload = {
        ...payload,
        attributes: [
          ...payload.attributes,
          {
            key: '_item_list_id',
            value: listId,
          },
          {
            key: '_item_list_name',
            value: listName,
          },
        ],
      };
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload,
        origin,
        component_name: isStylitics ? 'get_the_look_widget' : '',
      },
    });
    document.dispatchEvent(addToCartEvent);

    if (isStylitics) {
      const addToCartStyliticsEvent = new CustomEvent('stylitics:add-to-cart', {
        detail: {
          product,
          currentVariant,
        },
      });
      window.dispatchEvent(addToCartStyliticsEvent);
    }

    setMonogramData(false);
    const resetMonogramEvent = new CustomEvent('monogram:reset');
    document.dispatchEvent(resetMonogramEvent);
  };

  const selectText = `Select a ${(product?.options || [])
    .map((option) => (option?.name || '').toLowerCase())
    .find(
      (value) =>
        value !== 'color' &&
        !(selectedOptions || [])
          .map((option) => (option?.name || '').toLowerCase())
          .includes(value),
    )}`;

  const handleClick = (e) => {
    if (sizeSelected) {
      handleAddToCart(e, currentVariant);
    } else if (isSticky) {
      handleScrollToOptions();
    }
  };

  return (
    <div
      className={`grid grid-cols-[1fr_auto] py-4  ${
        (!currentVariant?.availableForSale && sizeSelected) ||
        Boolean(product.hideAddToCart)
          ? 'hidden'
          : null
      }`}
    >
      {
        <Fragment>
          <button
            type={
              !currentVariant?.availableForSale || !sizeSelected
                ? 'button'
                : 'submit'
            }
            data-close-quick-add
            className={`
              button button--primary group h-[42px] align-middle
              ${
                !sizeSelected
                  ? '!bg-gray-900 !text-text-inverse hover:!bg-[#1E1A18]'
                  : ''
              } ${
              !currentVariant?.availableForSale && sizeSelected
                ? 'hidden'
                : null
            }`}
            data-target="product-atc"
            {...(addToCartAttributeName && {
              'data-add-to-cart': addToCartAttributeName,
            })}
            onClick={handleClick}
            style={`${
              product.productType === 'EGift Card' ? 'display: none;' : ''
            }`}
          >
            {isMobile ? (
              sizeSelected ? (
                currentVariant.availableForSale ? (
                  `${window.TMW.Strings.product.addToCart}  ${
                    showPrice ? `| ${formatMoney(currentVariant.price)}` : ''
                  }`
                ) : (
                  window.TMW.Strings.product.outOfStock
                )
              ) : (
                selectText
              )
            ) : (
              <Fragment>
                <span
                  className={`h7--medium ${
                    sizeSelected ? '' : 'group-hover:hidden'
                  }`}
                >
                  {sizeSelected
                    ? currentVariant.availableForSale
                      ? window.TMW.Strings.product.addToCart
                      : window.TMW.Strings.product.outOfStock
                    : window.TMW.Strings.product.addToCart}
                </span>
                {!sizeSelected && (
                  <span className="hidden group-hover:block h7--medium">
                    {selectText}
                  </span>
                )}
              </Fragment>
            )}
          </button>
          {product.productType === 'EGift Card' && (
            <button className="GiftWizard-gift-button button button--primary group h-[42px] align-middle null gwbutton">
              {window.TMW.Strings.product.sendAsGift}
            </button>
          )}
        </Fragment>
      }
    </div>
  );
};

const ReviewStars = ({ yotpoApiKey, product }) => {
  const [stars, setStars] = useState(0);
  const [halfStars, setHalfStars] = useState(0);
  const [emptyStars, setEmptyStars] = useState(0);
  const [total, setTotal] = useState();
  const closePanelEvent = new CustomEvent('look-add:close');

  useEffect(async () => {
    if (!yotpoApiKey) return;
    // eslint-disable-next-line max-len
    const baseUrl = `https://api.yotpo.com/products/${yotpoApiKey}/${product.id
      .split('/Product/')
      .pop()}/bottomline`;

    const response = await fetch(baseUrl);
    const json = await response.json();

    if (json.status.code === 200) {
      const averageScore = Number(json.response?.bottomline?.average_score);
      const wholeStars = Math.floor(averageScore);
      const halfStar = !Number.isInteger(averageScore);
      const emptyStar = 5 - Math.ceil(averageScore);

      setStars(wholeStars);
      setHalfStars(halfStar ? 1 : 0);
      setEmptyStars(emptyStar);
      setTotal(json.response?.bottomline?.total_reviews);
    }
  }, [product]);

  const handleReviewClick = (e) => {
    e.preventDefault();

    const productUrl = `/products/${product.handle}`;

    if (window.location.pathname === productUrl) {
      document.dispatchEvent(closePanelEvent);

      const reviewsElement = document.getElementById('Reviews');

      if (reviewsElement) {
        reviewsElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.open(`${productUrl}#Reviews`, '_blank');
    }
  };

  return yotpoApiKey && total !== undefined ? (
    <a
      onClick={(e) => handleReviewClick(e)}
      className="mb-3.5 span:!text-core-3 flex gap-1.5 items-center"
    >
      <div className="flex gap-0.5 cursor-pointer">
        {[...Array(stars)]?.map((star) => (
          <IconStar key={star} />
        ))}
        {[...Array(halfStars)]?.map((star) => (
          <IconHalfStar key={star} classes="text-text-default" />
        ))}
        {[...Array(emptyStars)]?.map((star) => (
          <IconStar key={star} classes="text-gray-500" />
        ))}
      </div>
      <span className="body-3--underline text-text-subdued cursor-pointer">
        ({total} Reviews)
      </span>
    </a>
  ) : null;
};

const ProductForm = (props) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [monogramData, setMonogramData] = useState(false);
  const [sizeSelectedState, setSizeSelectedState] = useState(false);
  const [qty, setQty] = useState(1);
  const [breadcrumbData, setBreadcrumbData] = useState([]);
  const [isCallToOrder, setIsCallToOrder] = useState(false);
  const storefront = useMemo(() => new Storefront(), []);
  const globalSettingsEl = document.querySelector('[data-global-settings]');
  const globalSettings = JSON.parse(globalSettingsEl?.innerHTML || '{}');
  const inventoryThreshold = globalSettings?.inventoryThreshold || 15;
  const isStylitics = props?.isStylitics || false;
  const { addToCartAttributeName } = props;
  const appColorVariants =
    (props.activeProduct?.appColorVariants?.value &&
      JSON.parse(props.activeProduct?.appColorVariants?.value)) ||
    props.activeProduct?.metafields?.appColorVariants;
  const { discountCodeTag, finalSaleTag, dropShipTag } = JSON.parse(
    document.querySelector('[data-global-settings]').textContent,
  );

  const handleSeeDetails = (event) => {
    event.preventDefault();
    if (!isStylitics) {
      window.location.href = event.target.href;
      return;
    }

    const items = formatProductData(props.activeProduct);
    window.dataLayer.push({
      event: 'click_see_full_details',
      currency: Shopify.currency.active,
      component_name: 'get_the_look_widget',
      item_list_id: props?.listId,
      item_list_name: props?.listName,
      ecommerce: {
        items: [items],
      },
    });

    window.location.href = event.target.href;
  };

  if (!props.activeProduct) return null;

  const productTitle = formatProductTitle(
    props.activeProduct.title,
    props.activeProduct.options,
  );

  const sizeSelected = props.sizeSelected || sizeSelectedState;
  const setSizeSelected = props.setSizeSelected || setSizeSelectedState;

  useEffect(() => {
    if (!productGroups) return;
    const addProductGroups = () => {
      const promises = [];
      for (let i = 0; i < appColorVariants.length; i += 1) {
        promises.push(
          storefront.request({
            query: `query getSwatchProducts @inContext(country: ${window.Shopify.country}) {
            productByHandle(handle: "${appColorVariants[i]}") {
              ${fragments.product}
            }
          }`,
          }),
        );
      }
      Promise.all(promises).then((results) => {
        const responseProducts = results.map(
          (result) => result?.data?.productByHandle,
        );

        const filtered = responseProducts.filter((product) => product);

        const formattedWithMetafields = filtered.map((product) => {
          const filteredMetafields = product.metafields.filter(
            (field) => field,
          ); // filter out null values
          const formattedMetafields = filteredMetafields.reduce(
            (acc, field) => {
              if (!acc[field.namespace]) {
                acc[field.namespace] = {};
              }

              if (field.key === 'image') {
                acc[field.namespace][field.key] = field.reference.image.url;
              } else {
                acc[field.namespace][field.key] = field.value;
              }
              return acc;
            },
            {},
          );
          return {
            ...product,
            metafields: formattedMetafields,
            variants: edgesToNodes(product.variants, 'variant'),
          };
        });
        setProductGroups(formattedWithMetafields);
      });
    };

    if (appColorVariants && appColorVariants.length > 0) {
      addProductGroups();
    }
    setTimeout(() => {
      const rivoAccountWidget = document.getElementById(
        'rivo-account-slideout',
      );
      if (rivoAccountWidget) {
        window.RivoProfileAlpine.$data(
          rivoAccountWidget,
        ).initializeFavoriteButtons();
      }
    }, 200);
  }, [props.product]);

  const atcRef = useRef(null);
  const fitText = useMemo(
    () => <FitText product={props.activeProduct} />,
    [props.activeProduct, props.currentVariant],
  );

  const callToOrder = useMemo(() => (
    <div className="py-4">
      <a
        className="button button--primary no-underline h-[42px] w-full col-span-4"
        href={`tel:${window.TMW.Strings.callToOrder.phoneNumber}`}
      >
        {window.TMW.Strings.callToOrder.text}
      </a>
    </div>
  ));

  useEffect(() => {
    const updatedVariant = (
      props.activeProduct?.variants?.edges ||
      props.activeProduct?.variants ||
      []
    ).find(
      (v) =>
        JSON.stringify(
          (v?.node?.selectedOptions || v?.selectedOptions)?.filter(
            (item) => item.name.toLowerCase() !== 'color',
          ),
        ) ===
        JSON.stringify(
          selectedOptions.filter((item) => item.name.toLowerCase() !== 'color'),
        ),
    );

    if (updatedVariant)
      props.setCurrentVariant(updatedVariant?.node || updatedVariant);
    else if (!sizeSelected)
      props.setCurrentVariant(
        (props.activeProduct.variants.edges || props.activeProduct.variants)[0]
          .node ||
          (props.activeProduct.variants.edges ||
            props.activeProduct.variants)[0],
      );
  }, [selectedOptions]);

  useEffect(() => {
    setQty(1);
    if (props.activeProduct.tags.includes('Phone to order')) {
      setIsCallToOrder(true);
    }

    if (!props.activeProduct?.variantBySelectedOptions) {
      const firstVariant =
        (props.activeProduct.variants.edges || props.activeProduct.variants)[0]
          .node ||
        (props.activeProduct.variants.edges || props.activeProduct.variants)[0];

      props.setCurrentVariant(firstVariant);

      handleElevarDataPush(props.activeProduct);
    }

    const nonColorOptions = props.activeProduct.options.find(
      (option) => option.name.toLowerCase() !== 'color',
    );

    if (nonColorOptions.values.length === 1) {
      setSelectedOptions([
        {
          name: nonColorOptions.name,
          value: nonColorOptions.values[0],
        },
      ]);
      setSizeSelected(true);
    } else {
      setSelectedOptions([]);
      setSizeSelected(false);
    }
  }, [props.activeProduct]);

  useEffect(() => {
    if (!selectedOptions.length && sizeSelected) {
      setSelectedOptions([
        props.currentVariant.selectedOptions?.find(
          (option) => option.name.toLowerCase() === 'size',
        ),
      ]);
    }
  }, [props.currentVariant, selectedOptions, sizeSelected]);

  const handleOptionChange = ({ name, value }) => {
    setSizeSelected(true);
    setSelectedOptions([{ name, value }]);
  };

  const handleElevarDataPush = (newProduct) => {
    window.ElevarTransformFn = null;

    const productAvailability = {};

    newProduct.variants.forEach((variant) => {
      const sizeOption = variant.selectedOptions.find(
        (option) => option.name === 'Size',
      );

      if (sizeOption) {
        productAvailability[sizeOption.value] =
          variant.availableForSale === true ? 1 : 0;
      }
    });

    const availabilityString = JSON.stringify(productAvailability);

    window.ElevarTransformFn = null;

    const updatedCollection = `/collections/${
      newProduct.breadcrumbHandle?.value || ''
    }`;
    const updatedImage = (newProduct.images.edges || []).map(
      (edge) => `${edge.node.src}`,
    );
    const firstVariant =
      newProduct.variants.find((v) => v.availableForSale) ||
      newProduct.variants[0];
    const listItems = [
      {
        brand: newProduct.vendor,
        category: newProduct.productType,
        compare_at_price: String(
          newProduct.compareAtPriceRange.minVariantPrice.amount,
        ),
        id: firstVariant.sku,
        image: updatedImage[0],
        list: updatedCollection,
        name: newProduct.title,
        quantity: '1',
        price: String(newProduct.priceRange.minVariantPrice.amount),
        product_id: newProduct.id.split('gid://shopify/Product/')[1] || null,
        variant: firstVariant.title,
        variant_id:
          firstVariant.id.split('gid://shopify/ProductVariant/')[1] || null,
      },
    ];

    const eventData = {
      event: 'dl_view_item',
      user_properties: {},
      availability: availabilityString,
      ecommerce: {
        currencyCode: newProduct.priceRange.minVariantPrice.currencyCode,
        detail: {
          actionField: {
            list: updatedCollection,
            action: 'detail',
          },
          products: listItems,
        },
      },
    };
    window.ElevarDataLayer.push(eventData);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const variantId = searchParams.get('variant');

    if (props.activeProduct.productType === 'EGift Card') {
      setSelectedOptions([
        {
          name: props.activeProduct.variantBySelectedOptions.selectedOptions[0]
            .name,
          value:
            props.activeProduct.variantBySelectedOptions.selectedOptions[0]
              .value,
        },
      ]);
      setSizeSelected(true);
    }

    if (!variantId) return;

    const variant = (
      props.activeProduct?.variants?.edges || props.activeProduct?.variants
    )?.find((v) => (v.node || v).id.split('/ProductVariant/')[1] === variantId);

    if (!variant) return;

    props.setCurrentVariant(variant);

    variant.selectedOptions.forEach((opt) => {
      if (opt.name.toLowerCase() === 'color') return;

      const selectedOptionInput = document.querySelector(
        // eslint-disable-next-line max-len
        `[name="${props.activeProduct.id}_product-option-${opt.name}"][value="${opt.value}"]`,
      );

      if (!selectedOptionInput) {
        setSelectedOptions([]);
        setSizeSelected(false);
        return;
      }

      const currentOptions = [
        {
          name: selectedOptionInput?.dataset?.optionName,
          value: selectedOptionInput?.value,
        },
      ];

      setSizeSelected(true);
      setSelectedOptions(currentOptions);
    });
  }, []);

  const inventoryMessage = useMemo(
    () => (
      <InventoryMessage
        currentVariant={props.currentVariant}
        threshold={inventoryThreshold}
        sizeSelected={sizeSelected}
      />
    ),
    [props.currentVariant, sizeSelected],
  );

  // eslint-disable-next-line max-len
  const isMonogramOnlyProduct = props.activeProduct?.tags.includes(
    props.settings.customizableOnlyTag,
  );

  const variantSelectors = useMemo(
    () =>
      !props.activeProduct.hasOnlyDefaultVariant &&
      props.activeProduct.options.map((option) => (
        <VariantSelector
          currentVariant={props.currentVariant}
          key={`${option.name}-${props.activeProduct.id}`}
          option={option}
          setCurrentVariant={props.setCurrentVariant}
          variants={props.activeProduct.variants}
          product={props.activeProduct}
          setActiveProduct={props.setActiveProduct}
          handleOptionChange={handleOptionChange}
          selectedOptions={selectedOptions}
          activeProduct={props.activeProduct}
          chartPopup={props.chartPopup}
          isStylitics={isStylitics}
          inventoryMessage={{ inventoryMessage: () => inventoryMessage }}
          sizeLimit={props.sizeLimit}
          colorLimit={props.colorLimit}
          availableColorsArray={props.availableColorsArray}
          productGroups={productGroups}
          setQty={setQty}
          qty={qty}
        />
      )),
    [
      props.activeProduct,
      props.currentVariant,
      selectedOptions,
      productGroups,
      setQty,
      qty,
    ],
  );

  useEffect(() => {
    setBreadcrumbData({
      breadcrumbsLabel: props.activeProduct.breadcrumbsLabel,
      collectionBreadcrumbHandle:
        props.activeProduct.collectionBreadcrumbHandle,
      breadcrumbName: props.activeProduct.collectionBreadcrumbDisplayName,
    });
  }, []);

  const breadcrumbStatic = useMemo(
    () =>
      breadcrumbData.collectionBreadcrumbHandle &&
      breadcrumbData.breadcrumbName && (
        <nav role="navigation" aria-label={breadcrumbData.breadcrumbsLabel}>
          <ol className="flex flex-wrap pb-1.6 body-3 text-gray-800 mb-1.5">
            <li>
              <a
                className="no-underline"
                // eslint-disable-next-line max-len
                href={`${Shopify.routes.root}collections/${breadcrumbData.collectionBreadcrumbHandle}`}
              >
                {breadcrumbData.breadcrumbName}
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>{productTitle}</li>
          </ol>
        </nav>
      ),
    [breadcrumbData, productTitle],
  );

  const price = useMemo(
    () => (
      <Price
        currentVariant={props.currentVariant}
        product={props.activeProduct}
        finalSaleTag={finalSaleTag}
      />
    ),
    [props.currentVariant, props.activeProduct],
  );

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={props.currentVariant}
        selectedOptions={selectedOptions}
        product={props.activeProduct}
        sizeSelected={sizeSelected}
        discountCodeTag={discountCodeTag}
        finalSaleTag={finalSaleTag}
        dropShipTag={dropShipTag}
        monogramData={monogramData}
        setMonogramData={setMonogramData}
        isMonogramOnlyProduct={isMonogramOnlyProduct}
        isStylitics={isStylitics}
        addToCartAttributeName={addToCartAttributeName}
        listId={props.listId}
        listName={props.listName}
        qty={qty}
      />
    ),
    [
      props.currentVariant,
      props.activeProduct,
      selectedOptions,
      sizeSelected,
      monogramData,
      qty,
    ],
  );

  const reviewStars = useMemo(
    () => (
      <ReviewStars
        yotpoApiKey={props.settings.yotpoApiKey}
        product={props.activeProduct}
      />
    ),
    [props, props.activeProduct],
  );

  return (
    <form
      method="post"
      action="/cart/add"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate="novalidate"
      data-product-form
      className="pt-5 lg:pt-8"
    >
      <input
        type="hidden"
        name="id"
        value={props.currentVariant?.id.split('ProductVariant/')[1]}
      />
      <div
        data-current-product-id={props.activeProduct?.id.split('/Product/')[1]}
      ></div>
      <div className="border-b border-gray-300">
        {!props.hideBreadCrumbs && breadcrumbStatic}
        <div className="flex justify-between relative">
          <h5 className="h5--medium uppercase mb-1.5">
            {props.vendorCollection}
          </h5>
          <div className="favorite-button-wrapper">
            <rivo-favorite-button
              data-product-id={props.activeProduct?.id.split('/Product/')[1]}
              rivo-ax-data
              rivo-ax-cloak
              rivo-ax-html="$store.icons.heart"
              rivo-ax-show="$store.rivo_aw_settings?.favorites_enabled && (!$store.rivo_profile_customer || $store.loading_states.global_favorite_buttons)"
            ></rivo-favorite-button>
          </div>
        </div>

        <h1 className="product-title mb-1.5">{productTitle}</h1>
        <h4 className="mb-3.5">{price}</h4>
        <div className="">{reviewStars}</div>
      </div>
      {variantSelectors}
      {fitText}
      <div
        ref={atcRef}
        className={`w-full ${
          props.atcSticky
            ? 'absolute z-[5] bottom-0 w-full left-0 px-5 py-1 bg-white border-t border-gray-400 lg:border-none lg:left-auto lg:w-auto lg:relative lg:bottom-auto'
            : ''
        }`}
      >
        {isCallToOrder ? callToOrder : addToCartButton}
      </div>

      {props.showDetailsButton && (
        <a
          href={`${Shopify.routes.root}products/${props.activeProduct.handle}`}
          className="h6--medium block text-center my-4 no-underline lg:hidden"
          onClick={(event) => handleSeeDetails(event)}
        >
          {window.TMW.Strings.quickAdd.seeDetails}{' '}
          <span className="inline-block ml-1">
            <IconArrow direction="right" />
          </span>
        </a>
      )}

      {props.showDetailsButton && (
        <a
          href={`${Shopify.routes.root}products/${props.activeProduct.handle}`}
          className="h6--medium text-center my-4 no-underline hidden lg:block"
          onClick={(event) => handleSeeDetails(event)}
        >
          {window.TMW.Strings.quickAdd.seeDetails}{' '}
          <span className="inline-block ml-1">
            <IconArrow direction="right" />
          </span>
        </a>
      )}
    </form>
  );
};

export default ProductForm;
